import { EventEmitter } from '@angular/core';
import { DataSource } from '@angular/cdk/collections';
import { MatPaginator, PageEvent, MatSort, Sort } from '@angular/material';

import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { map } from 'rxjs/operators/map';
import { merge } from 'rxjs/observable/merge';
import { Observable } from 'rxjs/Observable';
import { Subject } from 'rxjs/Subject';
import { takeUntil } from 'rxjs/operators/takeUntil';

export class DataTableSource<T> extends DataSource<any> {
    private _database: DataTableDatabase<T>;
    private _filter: BehaviorSubject<string> = new BehaviorSubject<string>('');
    private _filteredData: T[];
    private _paginator: MatPaginator;
    private _sorter: MatSort;
    private _sorterFn: (a, b) => number;
    private _subrelease: Subject<void> = new Subject<void>();

    get data() {
        return <T[]>this._database.data;
    }
    get dataChange() {
        return this._database.dataChange;
    }
    get filter() {
        return this._filter.value;
    }
    set filter(value: string) {
        this._filter.next(value);
    }
    get filtereddata() {
        return <T[]>this._filteredData;
    }

    filterColumns: string[];
    FilteredDataUpdate: EventEmitter<T[]> = new EventEmitter<T[]>();

    constructor(
        init_data?: T[],
        init_paginator?: MatPaginator,
        init_sorter?: MatSort,
        init_sorterFn?: (a, b) => number,
        filterColumns?: string[]
    ) {
        super();

        this._database = new DataTableDatabase<T>(init_data || <T[]>[]);
        this._filteredData = this._database.data;
        this._paginator = init_paginator;
        this._sorter = init_sorter;
        this._sorterFn = init_sorterFn;
        this.filterColumns = filterColumns;

        // Set up a subscription on filter changes (if any)
        this._filter.pipe(takeUntil(this._subrelease)).subscribe(() => {
            if (this._paginator) {
                this._paginator.pageIndex = 0;
            }
        });
    }

    connect(): Observable<T[]> {
        let triggers: (BehaviorSubject<T[]> | BehaviorSubject<string> | EventEmitter<PageEvent> | EventEmitter<Sort>)[];

        triggers = [this._database.dataChange, this._filter];

        if (this._paginator != null) {
            triggers.push(this._paginator.page);
        }
        if (this._sorter != null) {
            triggers.push(this._sorter.sortChange);
        }

        return merge(...triggers).pipe(
            map(() => {
                let data = this._database.data.slice();

                // Do we need to filter this data before sorting?
                if (this.filterColumns) {
                    const filterText = this.filter.toLowerCase();

                    data = data.filter(item => {
                        let haveMatch = false;

                        this.filterColumns.every(fitem => {
                            const value = item[fitem];

                            if (value && (!filterText || (<string>value).toLowerCase().indexOf(filterText) >= 0)) {
                                haveMatch = true;
                                return false; // Stop looping
                            }
                            return true;
                        });

                        return haveMatch; // This is for the filter()
                    });
                }

                this._filteredData = data.slice();
                this.FilteredDataUpdate.emit(this._filteredData);

                // Do we need to sort this data before paginating?
                if (this._sorter && this._sorterFn && this._sorter.active && this._sorter.direction) {
                    data = data.sort(this._sorterFn);
                }

                const sorteddata = data.slice();

                if (this._paginator) {
                    const startIndex = this._paginator == null ? 0 : this._paginator.pageIndex * this._paginator.pageSize;

                    return sorteddata.splice(startIndex, this._paginator.pageSize);
                } else {
                    return sorteddata;
                }
            })
        );
    }

    disconnect() {}

    // #region Attach Sorter Method

    AttachSorter(_sorter: MatSort) {
        this._sorter = _sorter;
    }

    // #endregion

    // #region Update Method

    Update(_data: T[]) {
        this._database.dataChange.next(_data);
    }

    // #endregion
}

export class DataTableDatabase<T> {
    dataChange: BehaviorSubject<T[]> = new BehaviorSubject<T[]>([]);
    get data() {
        return <T[]>(this.dataChange.value || []);
    }

    constructor(_initdata?: T[]) {
        if (_initdata) {
            const _copied = _initdata.slice();
            this.dataChange.next(_copied);
        }
    }
}



// WEBPACK FOOTER //
// ./src/classes/DataTableSource/datatablesource.ts